import React, { useCallback, useEffect, useState } from "react";
import {
  DeleteTwoTone,
  EditTwoTone,
  EyeTwoTone,
  PlusOutlined,
  MinusCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import {
  Button,
  message,
  Table,
  Row,
  Col,
  Dropdown,
  Menu,
  Popconfirm,
  Modal,
  Form,
  Input,
  Select,
  Typography,
  Tag,
  Badge,
  Card,
  Space,
  Divider,
  Tooltip,
} from "antd";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ExtraContent from "../CommonComponents/ExtraContent";
import {
  getData,
  postData,
  deleteData,
  putData,
} from "../../../apiServices/common";
import {
  FABRIC_DEFECT_CATEGORY_LIST_ENDPOINT,
  FABRIC_DEFECT_CATEGORY_ADD_ENDPOINT,
  FABRIC_DEFECT_CATEGORY_EDIT_ENDPOINT,
  FABRIC_DEFECT_CATEGORY_DELETE_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { hasPermission } from "../../../utils/functions";
import {
  FABRIC_DEFECT_CATEGORY_CREATE_PERMISSION,
  FABRIC_DEFECT_CATEGORY_EDIT_PERMISSION,
  FABRIC_DEFECT_CATEGORY_DELETE_PERMISSION,
} from "../../../routes/permissions";
import { useLocation } from "react-router";

const { Title, Text } = Typography;
const { Option } = Select;

const FabricDefectCategoryConfig = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filter States
  const [filterCategoryName, setFilterCategoryName] = useState("");
  const [filterSubcategoryName, setFilterSubcategoryName] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Antd Form
  const [defectCategoryForm] = Form.useForm();

  // Filter open/close status update in localStorage
  useEffect(() => {
    const storedStatus = localStorage.getItem("filter_status");
    const parsed = storedStatus ? JSON.parse(storedStatus) : {};
    parsed[routerPath] = showFilter;
    localStorage.setItem("filter_status", JSON.stringify(parsed));
  }, [showFilter, routerPath]);

  // Fetch Category List
  const getAllCategories = useCallback(async () => {
    setLoading(true);

    const queryParams = new URLSearchParams({
      page: currentPage,
      per_page: pageSize,
    });

    if (filterCategoryName.trim()) {
      queryParams.append("name", filterCategoryName.trim());
    }
    if (filterSubcategoryName.trim()) {
      queryParams.append("subcategory_name", filterSubcategoryName.trim());
    }
    if (filterStatus) {
      queryParams.append("status", filterStatus);
    }

    try {
      const response = await getData(
        `${FABRIC_DEFECT_CATEGORY_LIST_ENDPOINT}?${queryParams.toString()}`,
        false,
      );

      if (response?.status === 200 && response?.data?.data) {
        const paginatedData = response.data.data;
        setTableData(paginatedData.data || []);
        setMetaData({
          defaultPageSize: paginatedData.per_page || 10,
          current: paginatedData.current_page || 1,
          defaultCurrent: paginatedData.current_page || 1,
          total: paginatedData.total || 0,
        });
      } else {
        setTableData([]);
        message.error("Failed to load fabric defect categories.");
      }
    } catch (err) {
      setTableData([]);
      message.error("Network error while fetching defect categories.");
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filterCategoryName, filterSubcategoryName, filterStatus]);

  // Load data on refresh or pagination change
  useEffect(() => {
    getAllCategories();
  }, [refresh, currentPage, pageSize, getAllCategories]);

  const refetch = () => {
    setRefresh((prev) => prev + 1);
  };

  // Filter actions
  const handleApplyFilter = () => {
    setCurrentPage(1);
    refetch();
  };

  const handleResetFilter = () => {
    setFilterCategoryName("");
    setFilterSubcategoryName("");
    setFilterStatus("");
    setCurrentPage(1);
    refetch();
  };

  // Modal handlers
  const onModalOpen = () => {
    setIsEdit(false);
    defectCategoryForm.resetFields();
    defectCategoryForm.setFieldsValue({
      name: "",
      status: "active",
      subcategories: [
        {
          name: "",
          description: "",
          status: "active",
        },
      ],
    });
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    defectCategoryForm.resetFields();
    setIsModalOpen(false);
    setIsEdit(false);
  };

  const openModalForEdit = (record) => {
    setIsEdit(true);

    const formattedSubcategories =
      record?.subcategories && record.subcategories.length > 0
        ? record.subcategories.map((sub) => ({
          id: sub.id,
          name: sub.name,
          description: sub.description || "",
          status: sub.status || "active",
        }))
        : [
          {
            name: "",
            description: "",
            status: "active",
          },
        ];

    defectCategoryForm.setFieldsValue({
      id: record?.id,
      name: record?.name,
      status: record?.status || "active",
      subcategories: formattedSubcategories,
    });

    setIsModalOpen(true);
  };

  const openViewModal = (record) => {
    setSelectedRecord(record);
    setIsViewModalOpen(true);
  };

  // Form Submit: Create or Edit
  const handleSubmit = async (values) => {
    if (!values.subcategories || values.subcategories.length === 0) {
      message.error("At least one subcategory is required.");
      return;
    }

    setSubmitting(true);

    const payload = {
      name: values.name,
      status: values.status,
      subcategories: values.subcategories.map((sub) => ({
        ...(sub.id ? { id: sub.id } : {}),
        name: sub.name,
        description: sub.description || null,
        status: sub.status || "active",
      })),
    };

    try {
      if (isEdit && values.id) {
        const response = await putData(
          `${FABRIC_DEFECT_CATEGORY_EDIT_ENDPOINT}/${values.id}`,
          payload,
        );

        if (response && (response.code === 200 || response.status === 200)) {
          message.success("Fabric Defect Category updated successfully!");
          onCloseModal();
          refetch();
        } else {
          const errMsg =
            response?.message?.error ||
            response?.message ||
            "Failed to update category.";
          message.error(typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg));
        }
      } else {
        const response = await postData(
          FABRIC_DEFECT_CATEGORY_ADD_ENDPOINT,
          payload,
        );

        if (response && (response.code === 200 || response.status === 200)) {
          message.success("Fabric Defect Category created successfully!");
          onCloseModal();
          refetch();
        } else {
          const errMsg =
            response?.message?.error ||
            response?.message ||
            "Failed to create category.";
          message.error(typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg));
        }
      }
    } catch (err) {
      message.error("Operation failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Action
  const deleteSelectedCategory = async (id) => {
    setLoading(true);
    try {
      const response = await deleteData(
        `${FABRIC_DEFECT_CATEGORY_DELETE_ENDPOINT}/${id}`,
      );

      if (response && (response.code === 200 || response.status === 200)) {
        message.success("Fabric Defect Category deleted successfully!");
        refetch();
      } else {
        message.error(response?.message || "Failed to delete category.");
      }
    } catch (err) {
      message.error("Failed to delete category.");
    } finally {
      setLoading(false);
    }
  };

  const changePage = (newPage, newPageSize) => {
    setCurrentPage(newPage);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }
    refetch();
  };

  // Table Columns
  const tableColumns = [
    {
      title: "SL",
      key: "sl",
      width: 60,
      align: "center",
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Defect Category Name",
      dataIndex: "name",
      key: "name",
      render: (name) => (
        <span style={{ fontWeight: 600, color: "#1890ff" }}>
          <TagsOutlined style={{ marginRight: 6 }} />
          {name}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={status === "active" || status === "1" ? "green" : "orange"}>
          {status === "active" || status === "1" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Subcategories",
      key: "subcategories",
      render: (_, record) => {
        const subs = record.subcategories || [];
        if (subs.length === 0) {
          return <Text type="secondary">None</Text>;
        }
        const visibleSubs = subs.slice(0, 3);
        const remaining = subs.length - 3;

        return (
          <Space wrap size={[4, 4]}>
            {visibleSubs.map((sub) => (
              <Tag
                key={sub.id}
                color={sub.status === "active" || sub.status === "1" ? "blue" : "default"}
              >
                {sub.name}
              </Tag>
            ))}
            {remaining > 0 && (
              <Badge
                count={`+${remaining} more`}
                style={{
                  backgroundColor: "#52c41a",
                  cursor: "pointer",
                }}
                onClick={() => openViewModal(record)}
              />
            )}
          </Space>
        );
      },
    },
    {
      title: "Subcategory Count",
      key: "subcategories_count",
      width: 130,
      align: "center",
      render: (_, record) => (
        <Badge
          count={record.subcategories ? record.subcategories.length : record.subcategories_count || 0}
          showZero
          style={{ backgroundColor: "#108ee9" }}
        />
      ),
    },
    {
      title: "Created By",
      dataIndex: ["created_by", "name"],
      key: "created_by",
      render: (name) => name || <Text type="secondary">System</Text>,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (date) => (date ? new Date(date).toLocaleDateString() : "N/A"),
    },
    {
      title: "Action",
      key: "action",
      width: 110,
      align: "center",
      render: (_, record) => {
        const canEdit = hasPermission([FABRIC_DEFECT_CATEGORY_EDIT_PERMISSION]);
        const canDelete = hasPermission([FABRIC_DEFECT_CATEGORY_DELETE_PERMISSION]);

        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    key: "view",
                    label: (
                      <span onClick={() => openViewModal(record)}>
                        <EyeTwoTone twoToneColor="#52c41a" />
                        <span style={{ marginLeft: 6 }}>View Details</span>
                      </span>
                    ),
                  },
                  {
                    key: "edit",
                    disabled: !canEdit,
                    label: (
                      <span onClick={() => openModalForEdit(record)}>
                        <EditTwoTone />
                        <span style={{ marginLeft: 6 }}>Edit</span>
                      </span>
                    ),
                  },
                  {
                    key: "delete",
                    disabled: !canDelete,
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete this category and its subcategories?"
                        okText="Yes"
                        cancelText="No"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => deleteSelectedCategory(record?.id)}
                      >
                        <span>
                          <DeleteTwoTone twoToneColor="#f5222d" />
                          <span style={{ color: "#f5222d", marginLeft: 6 }}>
                            Delete
                          </span>
                        </span>
                      </Popconfirm>
                    ),
                  },
                ]}
              />
            }
          />
        );
      },
    },
  ];

  // Nested row expanded table
  const expandedRowRender = (record) => {
    const subColumns = [
      {
        title: "SL",
        key: "sub_sl",
        width: 50,
        render: (_, __, i) => i + 1,
      },
      {
        title: "Subcategory Name",
        dataIndex: "name",
        key: "name",
        render: (text) => <Text strong>{text}</Text>,
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        render: (desc) => desc || <Text type="secondary">No description</Text>,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (st) => (
          <Tag color={st === "active" || st === "1" ? "green" : "orange"}>
            {st === "active" || st === "1" ? "Active" : "Inactive"}
          </Tag>
        ),
      },
    ];

    return (
      <div style={{ margin: "4px 0", padding: "10px 14px", background: "#f9fafb", borderRadius: 6 }}>
        <Text strong style={{ display: "block", marginBottom: 8, color: "#4b5563" }}>
          Defect Subcategories ({record?.subcategories?.length || 0}):
        </Text>
        <Table
          columns={subColumns}
          dataSource={record?.subcategories || []}
          pagination={false}
          rowKey="id"
          size="small"
          bordered
        />
      </div>
    );
  };

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            title={
              <Title level={5} style={{ margin: 0 }}>
                Fabric Defect Category Configuration
              </Title>
            }
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([FABRIC_DEFECT_CATEGORY_CREATE_PERMISSION])}
                addButtonText={
                  hasPermission([FABRIC_DEFECT_CATEGORY_CREATE_PERMISSION])
                    ? "Add New Category"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
          />
        }
        content={
          <>
            {/* Filter Card */}
            {showFilter && (
              <Card
                size="small"
                style={{
                  marginBottom: 16,
                  borderRadius: 8,
                  backgroundColor: "#fafafa",
                  border: "1px solid #f0f0f0",
                }}
              >
                <Row gutter={[16, 12]} align="middle">
                  <Col xs={24} sm={12} md={7}>
                    <Text strong>Category Name</Text>
                    <Input
                      placeholder="Search Category Name"
                      value={filterCategoryName}
                      onChange={(e) => setFilterCategoryName(e.target.value)}
                      onPressEnter={handleApplyFilter}
                      allowClear
                      style={{ marginTop: 4 }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={7}>
                    <Text strong>Subcategory Name</Text>
                    <Input
                      placeholder="Search Subcategory Name"
                      value={filterSubcategoryName}
                      onChange={(e) => setFilterSubcategoryName(e.target.value)}
                      onPressEnter={handleApplyFilter}
                      allowClear
                      style={{ marginTop: 4 }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={5}>
                    <Text strong>Status</Text>
                    <Select
                      value={filterStatus}
                      onChange={(val) => setFilterStatus(val)}
                      style={{ width: "100%", marginTop: 4 }}
                    >
                      <Option value="">All Statuses</Option>
                      <Option value="active">Active</Option>
                      <Option value="inactive">Inactive</Option>
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} md={5} style={{ display: "flex", gap: 8, marginTop: 22 }}>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      onClick={handleApplyFilter}
                    >
                      Search
                    </Button>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={handleResetFilter}
                    >
                      Reset
                    </Button>
                  </Col>
                </Row>
              </Card>
            )}

            {/* Main Table */}
            <div className="table" style={{ marginTop: 10 }}>
              <Table
                loading={loading}
                dataSource={tableData}
                columns={tableColumns}
                rowKey="id"
                expandable={{
                  expandedRowRender,
                  rowExpandable: (record) =>
                    record.subcategories && record.subcategories.length > 0,
                }}
                pagination={{
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  defaultPageSize: metaData?.defaultPageSize || pageSize,
                  current: metaData?.current || currentPage,
                  total: metaData?.total || 0,
                  showTotal: (total, range) =>
                    `Showing ${range[0]}-${range[1]} of ${total} categories`,
                  onChange: changePage,
                }}
              />
            </div>
          </>
        }
      />

      {/* Create / Edit Modal */}
      <Modal
        open={isModalOpen}
        title={
          <Space>
            <TagsOutlined style={{ color: "#1890ff" }} />
            <span>
              {isEdit
                ? "Edit Fabric Defect Category"
                : "Create Fabric Defect Category"}
            </span>
          </Space>
        }
        onCancel={onCloseModal}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        width={780}
        footer={[
          <Button key="cancel" onClick={onCloseModal}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitting}
            onClick={() => defectCategoryForm.submit()}
          >
            {isEdit ? "Update Category" : "Save Category"}
          </Button>,
        ]}
      >
        <Form
          form={defectCategoryForm}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item name="id" hidden />

          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item
                name="name"
                label={<Text strong>Defect Category Name</Text>}
                rules={[
                  {
                    required: true,
                    message: "Category name is required",
                  },
                ]}
              >
                <Input placeholder="e.g. Weaving Defect, Yarn Defect, Dyeing Defect" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item
                name="status"
                label={<Text strong>Status</Text>}
                initialValue="active"
                rules={[{ required: true, message: "Status is required" }]}
              >
                <Select>
                  <Option value="active">
                    <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 4 }} />
                    Active
                  </Option>
                  <Option value="inactive">
                    <CloseCircleOutlined style={{ color: "#f5222d", marginRight: 4 }} />
                    Inactive
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: "12px 0 16px" }}>
            <Space>
              <Text strong style={{ fontSize: 14 }}>
                Dynamic Subcategories
              </Text>
              <Badge count="Required" style={{ backgroundColor: "#faad14" }} />
            </Space>
          </Divider>

          {/* Dynamic Subcategories Form List */}
          <Form.List
            name="subcategories"
            rules={[
              {
                validator: async (_, subcategories) => {
                  if (!subcategories || subcategories.length < 1) {
                    return Promise.reject(
                      new Error("Please add at least one subcategory"),
                    );
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Card
                    key={key}
                    size="small"
                    style={{
                      marginBottom: 10,
                      backgroundColor: "#fafbfc",
                      borderRadius: 6,
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <Row gutter={12} align="middle">
                      <Form.Item
                        {...restField}
                        name={[name, "id"]}
                        hidden
                      >
                        <Input />
                      </Form.Item>

                      <Col xs={24} sm={10}>
                        <Form.Item
                          {...restField}
                          name={[name, "name"]}
                          label={index === 0 ? "Subcategory Name" : ""}
                          rules={[
                            {
                              required: true,
                              message: "Subcategory name is required",
                            },
                          ]}
                          style={{ marginBottom: 4 }}
                        >
                          <Input placeholder="e.g. Broken End, Hole, Miss Print" />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={8}>
                        <Form.Item
                          {...restField}
                          name={[name, "description"]}
                          label={index === 0 ? "Description" : ""}
                          style={{ marginBottom: 4 }}
                        >
                          <Input placeholder="Description (optional)" />
                        </Form.Item>
                      </Col>

                      <Col xs={18} sm={5}>
                        <Form.Item
                          {...restField}
                          name={[name, "status"]}
                          label={index === 0 ? "Status" : ""}
                          initialValue="active"
                          style={{ marginBottom: 4 }}
                        >
                          <Select>
                            <Option value="active">Active</Option>
                            <Option value="inactive">Inactive</Option>
                          </Select>
                        </Form.Item>
                      </Col>

                      <Col
                        xs={6}
                        sm={1}
                        style={{
                          textAlign: "center",
                          paddingTop: index === 0 ? 20 : 0,
                        }}
                      >
                        <Tooltip title="Remove Subcategory">
                          <Button
                            type="text"
                            danger
                            icon={<MinusCircleOutlined />}
                            disabled={fields.length === 1}
                            onClick={() => remove(name)}
                          />
                        </Tooltip>
                      </Col>
                    </Row>
                  </Card>
                ))}

                <Form.ErrorList errors={errors} orientation="center" />

                <Form.Item style={{ marginTop: 8 }}>
                  <Button
                    type="dashed"
                    onClick={() =>
                      add({
                        name: "",
                        description: "",
                        status: "active",
                      })
                    }
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Subcategory
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* View Details Modal */}
      <Modal
        open={isViewModalOpen}
        title={
          <Space>
            <EyeTwoTone twoToneColor="#1890ff" />
            <span>Category Details</span>
          </Space>
        }
        onCancel={() => setIsViewModalOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsViewModalOpen(false)}>
            Close
          </Button>,
        ]}
        width={680}
      >
        {selectedRecord && (
          <div>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Text type="secondary">Category Name:</Text>
                <div>
                  <Text strong style={{ fontSize: 16, color: "#1890ff" }}>
                    {selectedRecord.name}
                  </Text>
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Status:</Text>
                <div>
                  <Tag
                    color={
                      selectedRecord.status === "active" ||
                        selectedRecord.status === "1"
                        ? "green"
                        : "orange"
                    }
                  >
                    {selectedRecord.status === "active" ||
                      selectedRecord.status === "1"
                      ? "Active"
                      : "Inactive"}
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Created By:</Text>
                <div>
                  <Text>{selectedRecord?.createdBy?.name || "System"}</Text>
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Created At:</Text>
                <div>
                  <Text>
                    {selectedRecord.created_at
                      ? new Date(selectedRecord.created_at).toLocaleString()
                      : "N/A"}
                  </Text>
                </div>
              </Col>
            </Row>

            <Divider orientation="left" style={{ margin: "8px 0 14px" }}>
              Subcategories ({selectedRecord?.subcategories?.length || 0})
            </Divider>

            <Table
              dataSource={selectedRecord?.subcategories || []}
              rowKey="id"
              pagination={false}
              size="small"
              bordered
              columns={[
                {
                  title: "#",
                  key: "sl",
                  width: 50,
                  align: "center",
                  render: (_, __, i) => i + 1,
                },
                {
                  title: "Subcategory Name",
                  dataIndex: "name",
                  key: "name",
                  render: (text) => <Text strong>{text}</Text>,
                },
                {
                  title: "Description",
                  dataIndex: "description",
                  key: "description",
                  render: (desc) => desc || <Text type="secondary">None</Text>,
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  width: 100,
                  align: "center",
                  render: (st) => (
                    <Tag color={st === "active" || st === "1" ? "green" : "orange"}>
                      {st === "active" || st === "1" ? "Active" : "Inactive"}
                    </Tag>
                  ),
                },
              ]}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default FabricDefectCategoryConfig;
